import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { liveCardFromStream } from "@/components/todos/tray/live-cards";
import { useApprovalWaitingNotice } from "@/components/kory/use-approval-waiting-notice";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { streamSSE, type PlanEntry, type SSEEvent } from "@/lib/sse";
import { fetchApi } from "@/lib/api";
import { KORY_CONTENT_ENCODING, encodeBase64Utf8 } from "@/lib/content-encoding";
import { capturePageContext, type PageContext } from "@/lib/kory-page-context";
import {
  filesPayload,
  imagesPayload,
  serializeAttachmentsBlock,
  toMessageAttachments,
  type KoryComposerAttachment,
  type KoryMessageAttachment,
} from "@/lib/kory-attachments";
import {
  cellFromCaptureTarget,
  KORY_LONG_PRESS_MOVE_PX,
  KORY_LONG_PRESS_MS,
} from "@/lib/kory-capture-pointer";
import {
  type KoryCellContext,
  sameCell,
  serializeCellContextBlock,
} from "@/lib/kory-cell-context";
import { bridgedCellMenuFromMessage, postCellMenuToParent } from "@/lib/kory-embed-bridge";
import { useAuth } from "@/contexts/auth-context";
import { resolvePageOwner } from "@/lib/nav-v2/catalog";
import { settleRunningToolCall, toolCallDurationMs } from "@/components/kory/kory-tool-card-helpers";

export type FeedbackRating = "up" | "down";

/** Per-call token counts when router-acp attributes usage to a tool call. */
export interface ToolCallTokens {
  input: number;
  output: number;
}

/**
 * Structured ACP content blocks preserved through the relay (diff / text).
 * Optional — older history only has the flattened `preview` string.
 */
export interface ToolContentBlock {
  type: string;
  path?: string;
  oldText?: string;
  newText?: string;
  content?: { type?: string; text?: string };
}

export interface ToolCallStatus {
  id?: string;
  tool: string;
  status: "running" | "complete" | "error" | "cancelled";
  preview?: string;
  input?: Record<string, unknown> | null;
  // ACP tool category (execute/read/edit/search/…) when the source reports
  // one — drives specialized action cards (terminal / edit / search / …).
  kind?: string | null;
  // Per-call router-acp attribution (feature-detected; absent → UI hides).
  model?: string | null;
  reason?: string | null;
  tokens?: ToolCallTokens | null;
  // Structured result/body blocks (diff path+old/new, terminal text, …).
  contentBlocks?: ToolContentBlock[] | null;
  // Set on a tool_start from the live stream. History omits it, so a running
  // tool reloaded from history does not paint the routing chip.
  live?: boolean;
  // Wall-clock start (ISO) + resolved duration for the card rail.
  startedAt?: string | null;
  durationMs?: number | null;
}

// How many cells can ride along on one message. The serialized `<cell-context>`
// block is a JSON array, so multiple cells let the user ask Kory to compare them
// (e.g. "why is Bell ahead of AT here?"). Capped so the block stays small and the
// chip row stays legible; past the cap the oldest captured cell drops off.
const MAX_ATTACHED_CELLS = 6;


// A paused write awaiting the user's hard confirm. The backend authors the
// summary (destination + provenance, never content) and the paused tool call
// resumes only when the decision row is written — the card is the click that
// writes it.
export interface WriteConfirmationState {
  id: string;
  tool: string;
  summary: string;
  timeoutSeconds: number;
  // Whether the card offers "Always approve for this chat".
  canRemember: boolean;
  // "pending" while the backend waits; "deciding" between our POST and the
  // stream's resolved event; then the settled decision. "auto" is a write a
  // chat grant approved without pausing. "stopped" is an explicit Stop.
  status: "pending" | "deciding" | "allow" | "deny" | "expired" | "auto" | "stopped";
  // A card from history for a pause no stream is waiting on: deciding it
  // delivers the answer server-side, and the turn is then followed from history.
  deliver?: boolean;
}

/** A labeled failover / switch / retry notice, rendered as a divider. */
export interface RouterNoticeInfo {
  /** `switch` (moved to another model), `retry`, or `retry_wait`. */
  kind: "switch" | "retry" | "retry_wait";
  /** Divider label, e.g. `GPT-6.1 Sol outage`. */
  label: string;
  /** Collapsed divider text, e.g. `GPT-6.1 Sol outage: switching to Opus`. */
  headline: string;
  /** Full router block (and any raw provider error) for the expanded view. */
  detail: string;
  /** When a `retry_wait` notice retries the old model (ISO 8601). */
  retryAt?: string | null;
}

export interface KoryMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  toolCalls?: ToolCallStatus[];
  // Kory Code only: the coding agent's todo list as of this turn (ACP `plan`
  // session updates — TodoWrite/Task*). Each update is a full snapshot, so
  // this always holds the turn's latest rather than an accumulated history.
  plan?: PlanEntry[];
  // Paused writes on this assistant turn: from the live stream, or from
  // history when one is still undecided, was raised after the stream gave
  // up, or expired unanswered. Other settled decisions stay in the audit row.
  writeConfirmations?: WriteConfirmationState[];
  // Shown while the turn keeps working past the stream's time budget.
  workingNote?: string;
  pageContext?: PageContext;
  cellContext?: KoryCellContext[];
  // Composer attachments (files/images/pasted text) on a user turn — chips
  // only; the payloads ride the outgoing request, not message state. History
  // reloads re-hydrate these from the backend's `attachments` echo (without
  // image previews — the bytes aren't echoed back).
  attachments?: KoryMessageAttachment[];
  timestamp: Date;
  // Last visible-progress time on an in-flight assistant turn (text/tool/
  // plan/notice). Kory Code stamps this from live events and foldHistory;
  // unset means no activity since creation. The in-app Kory history API
  // does not persist it.
  updatedAt?: Date;
  // When an assistant turn finished working. Unset while it is still in
  // progress or for legacy history that never recorded a close time.
  finishedAt?: Date;
  // Model that produced an assistant turn (e.g. "claude-haiku-4-5"). Unset
  // for user turns and for legacy assistant history whose parent model cannot
  // be reconstructed. Kory Code stamps it from main-session model events;
  // per-request tool/delegate models stay on their own rows.
  model?: string;
  // The user's thumbs vote on an assistant turn, or unset if they haven't
  // voted. Drives the active state of the message action toolbar.
  feedback?: FeedbackRating;
  // Stream-resilience state for an assistant turn whose SSE connection dropped
  // (closed without a `done` event): "reconnecting" while we reconcile the turn
  // from server-persisted history with bounded backoff; "lost" if every attempt
  // failed (e.g. still offline). Unset on healthy turns.
  reconnect?: "reconnecting" | "lost";
  // The user's text for a dropped turn, kept so a "lost" turn can offer a
  // one-tap retry (re-send) — the live stream itself can't be resumed.
  retryContent?: string;
  // Set on the assistant turn whose send transparently migrated the
  // conversation onto the user's newly preferred model (backend
  // `model_upgraded` SSE event, or a Kory Code router switch — the latter IS
  // rehydrated from history). Renders as an inline "Now using X" divider
  // above the turn. `pending` marks a switch the user requested this send but
  // the router hasn't confirmed yet — the divider shows "Switching to X…" with
  // a spinner until the `model` event (or the turn ending) settles it.
  modelUpgrade?: { from?: string | null; to?: string | null; pending?: boolean };
  // Kory Code: router-acp failover / cordon / retry notices peeled out of this
  // turn's text. Each renders as an expandable divider above the turn; a
  // `switch` notice replaces the plain "Now using X" divider.
  routerNotices?: RouterNoticeInfo[];
  // Generic break line rendered above this message (same visual as
  // modelUpgrade) — Kory Code stamps it where a conversation resumed on a
  // fresh agent session, delineating the two sessions.
  divider?: { label: string };
  // Kory Code user turns only: who sent it ("relay" = a relay wake, nudge, or
  // parent-coordinator message) and, for canned/automated prompts, what it is
  // (see kory-code/wake-runs.ts). A set `wakeKind` renders as a system chip.
  origin?: "human" | "relay";
  wakeKind?: string;
  // Kory session turns only (history `kind` / `briefDate`): the day's brief,
  // a card tap in the tray, or an event that woke Kory.
  kind?: "brief" | "action" | "event" | null;
  briefDate?: string | null;
  // Who sent a user turn in an Interactive App's shared conversation (its
  // owner or an editor). From history; unset everywhere else.
  sender?: KoryMessageSender | null;
}

export interface KoryMessageSender {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

interface RawHistoryMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  toolCalls?: ToolCallStatus[];
  pageContext?: PageContext;
  cellContext?: KoryCellContext[];
  attachments?: KoryMessageAttachment[];
  timestamp: string;
  finishedAt?: string;
  model?: string;
  feedback?: FeedbackRating;
  kind?: "brief" | "action" | "event" | null;
  briefDate?: string | null;
  writeConfirmations?: WriteConfirmationState[] | null;
  sender?: KoryMessageSender | null;
}

interface ConversationListResponse {
  conversations: {
    id: string;
    title: string;
    pinned: boolean;
    archived: boolean;
    created_at: string;
    updated_at: string;
  }[];
}

function parseHistoryMessages(raw: RawHistoryMessage[]): KoryMessage[] {
  return raw.map((m) => ({
    id: m.id || crypto.randomUUID(),
    role: m.role,
    content: m.content,
    toolCalls: m.toolCalls,
    pageContext: m.pageContext,
    cellContext: m.cellContext,
    attachments: m.attachments,
    timestamp: new Date(m.timestamp),
    finishedAt: m.finishedAt ? new Date(m.finishedAt) : undefined,
    model: m.model,
    feedback: m.feedback,
    kind: m.kind,
    briefDate: m.briefDate,
    ...(m.sender ? { sender: m.sender } : {}),
    ...(m.writeConfirmations?.length ? { writeConfirmations: m.writeConfirmations } : {}),
  }));
}

// /history reconcile schedule: a few fast retries (a genuine drop is usually
// already finished server-side), then — while the server still reports the
// turn `complete: false` — a steady poll sized to outlast the longest turn: an
// App Upgrade/Reconvert (judge rounds plus renders) can run 15+ minutes. ~1h
// keeps a wedged run from polling forever; past it the partial stays on screen.
const RECONCILE_FAST_BACKOFFS = [800, 1600, 3200, 5000];
const RECONCILE_SLOW_POLL_MS = 8000;
const RECONCILE_SLOW_POLL_COUNT = 450;
/** Resume attempts after a dropped or busy reader before falling back to /history. */
export const MAX_REJOINS = 3;

const SETTLED_CONFIRMATION = new Set(["allow", "deny", "expired", "auto", "stopped"]);
const STOP_MISSED_MARK = "Stop did not reach Kory";
const STOP_MISSED_WARNING = "**Error:** Stop did not reach Kory. Open this chat again to continue.";

interface TurnBinding {
  conversationId: string | null;
  sessionId: string | null;
  turnToken: string | null;
  scanCursor: string | null;
  relayedThrough: string | null;
}

interface PendingStop {
  assistantId: string | null;
  ordinal: number | null;
  conversationId: string | null;
}

function withStopMissedWarning(message: KoryMessage): KoryMessage {
  if (message.content.includes(STOP_MISSED_MARK)) return message;
  return { ...message, content: `${message.content}\n\n${STOP_MISSED_WARNING}` };
}

// History refresh gives the stopped answer a server id. The warning was painted
// on the local id, so the reread has to keep it on that same assistant slot.
function keepStopMissedWarnings(prev: KoryMessage[], next: KoryMessage[]): KoryMessage[] {
  const nextAssistants = next.filter((message) => message.role === "assistant");
  const warned = new Set<string>();
  prev
    .filter((message) => message.role === "assistant")
    .forEach((message, ordinal) => {
      if (!message.content.includes(STOP_MISSED_MARK)) return;
      const target = nextAssistants[ordinal];
      if (target) warned.add(target.id);
    });
  if (warned.size === 0) return next;
  return next.map((message) => (warned.has(message.id) ? withStopMissedWarning(message) : message));
}

/** The reread thread, or what is on screen when nothing in it changed: a refresh that finds no new turn must not
 *  re-render the thread (and scroll a reader of older messages back to the bottom). */
function unlessUnchanged(prev: KoryMessage[], next: KoryMessage[]): KoryMessage[] {
  return prev.length === next.length && JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
}

function emptyTurnBinding(turnToken: string | null, conversationId: string | null): TurnBinding {
  return { conversationId, sessionId: null, turnToken, scanCursor: null, relayedThrough: null };
}

function confirmationDecision(decision: string | undefined): WriteConfirmationState["status"] | null {
  if (decision === "allow" || decision === "deny" || decision === "expired" || decision === "stopped") return decision;
  return null;
}

/** Text, tools, and confirmation cards shared by the live send and a resume window. */
function applyRelayEvent(message: KoryMessage, event: SSEEvent): KoryMessage {
  if (event.type === "text") {
    return { ...message, content: message.content + (event.content ?? "") };
  }
  if (event.type === "tool_start") {
    if (event.id && message.toolCalls?.some((tc) => tc.id === event.id)) return message;
    return {
      ...message,
      toolCalls: [
        ...(message.toolCalls ?? []),
        {
          id: event.id,
          tool: event.tool!,
          status: "running" as const,
          input: event.input,
          kind: event.kind ?? null,
          model: event.model ?? null,
          reason: event.reason ?? null,
          tokens: event.tokens ?? null,
          contentBlocks: event.contentBlocks ?? null,
          startedAt: event.ts ?? new Date().toISOString(),
          live: true,
        },
      ],
    };
  }
  if (event.type === "tool_result") {
    let matched = false;
    const toolCalls = (message.toolCalls ?? []).map((tc) => {
      if (matched) return tc;
      const idMatch = event.tool_use_id && tc.id === event.tool_use_id;
      const nameMatch = !event.tool_use_id && tc.tool === event.tool && tc.status === "running";
      if (!idMatch && !nameMatch) return tc;
      matched = true;
      const durationMs = toolCallDurationMs(tc.startedAt, event.ts ?? Date.now()) ?? tc.durationMs;
      return {
        ...tc,
        status: event.status === "error" ? ("error" as const) : ("complete" as const),
        preview: event.preview ?? tc.preview,
        model: event.model ?? tc.model,
        reason: event.reason ?? tc.reason,
        tokens: event.tokens ?? tc.tokens,
        contentBlocks: event.contentBlocks ?? tc.contentBlocks,
        durationMs,
      };
    });
    return { ...message, toolCalls };
  }
  if (event.type === "write_confirmation" || event.type === "write_confirmation_auto") {
    const id = event.id ?? "";
    const existing = message.writeConfirmations ?? [];
    const prior = existing.find((wc) => wc.id === id);
    if (prior && SETTLED_CONFIRMATION.has(prior.status)) return message;
    const auto = event.type === "write_confirmation_auto";
    const next = {
      id,
      tool: (event.tool as string) ?? prior?.tool ?? "",
      summary: (event.summary as string) ?? prior?.summary ?? "",
      timeoutSeconds: auto ? 0 : ((event.timeout_seconds as number) ?? prior?.timeoutSeconds ?? 300),
      canRemember: auto ? false : event.can_remember === true || prior?.canRemember === true,
      status: auto ? ("auto" as const) : (prior?.status ?? ("pending" as const)),
    };
    return {
      ...message,
      writeConfirmations: prior ? existing.map((wc) => (wc.id === id ? next : wc)) : [...existing, next],
    };
  }
  if (event.type === "write_confirmation_pending") {
    // Keepalive while the reader waits on the person. A card the confirmation
    // event already opened stays as it is. A keepalive that arrives without
    // that event still has to show an answerable card.
    const id = event.id ?? "";
    if (!id) return message;
    const existing = message.writeConfirmations ?? [];
    if (existing.some((wc) => wc.id === id)) return message;
    return {
      ...message,
      writeConfirmations: [
        ...existing,
        {
          id,
          tool: (event.tool as string) ?? "",
          summary: (event.summary as string) ?? "",
          timeoutSeconds: (event.timeout_seconds as number) ?? 300,
          canRemember: event.can_remember === true,
          status: "pending" as const,
        },
      ],
    };
  }
  if (event.type === "write_confirmation_resolved") {
    const id = event.id ?? "";
    const decision = confirmationDecision(event.decision) ?? "expired";
    const existing = message.writeConfirmations ?? [];
    if (!existing.some((wc) => wc.id === id)) {
      return {
        ...message,
        writeConfirmations: [
          ...existing,
          {
            id,
            tool: (event.tool as string) ?? "",
            summary: (event.summary as string) ?? "",
            timeoutSeconds: (event.timeout_seconds as number) ?? 300,
            canRemember: false,
            status: decision,
          },
        ],
      };
    }
    return {
      ...message,
      writeConfirmations: existing.map((wc) => (wc.id === id ? { ...wc, status: decision } : wc)),
    };
  }
  return message;
}

function settleOpenTurn(message: KoryMessage, toolStatus: ToolCallStatus["status"], cardStatus: WriteConfirmationState["status"]): KoryMessage {
  const toolCalls = message.toolCalls?.some((tc) => tc.status === "running")
    ? message.toolCalls.map((tc) => settleRunningToolCall(tc, toolStatus))
    : message.toolCalls;
  const writeConfirmations = message.writeConfirmations?.some((wc) => wc.status === "pending" || wc.status === "deciding")
    ? message.writeConfirmations.map((wc) =>
        wc.status === "pending" || wc.status === "deciding" ? { ...wc, status: cardStatus } : wc,
      )
    : message.writeConfirmations;
  if (toolCalls === message.toolCalls && writeConfirmations === message.writeConfirmations) return message;
  return { ...message, toolCalls, writeConfirmations };
}

function resumeRequestBody(conversationId: string, binding: TurnBinding) {
  // A binding belongs to one conversation. Opening another must not resume
  // with the previous turn token, session, cursor, or watermark.
  const same = binding.conversationId === conversationId;
  return {
    messages: [] as [],
    resume: true as const,
    conversation_id: conversationId,
    ...(same && binding.turnToken ? { turn_token: binding.turnToken } : {}),
    ...(same && binding.sessionId ? { provider_session_id: binding.sessionId } : {}),
    ...(same && binding.scanCursor ? { scan_cursor: binding.scanCursor } : {}),
    ...(same && binding.relayedThrough ? { relayed_through: binding.relayedThrough } : {}),
  };
}

function rememberRelayProgress(binding: TurnBinding, event: SSEEvent) {
  if (event.event_id) binding.relayedThrough = event.event_id;
  if (event.session_id) binding.sessionId = event.session_id;
  if (event.turn_token) binding.turnToken = event.turn_token;
  if (event.type === "done" && event.incomplete) {
    binding.scanCursor = event.scan_cursor ?? null;
  } else if (event.scan_cursor) {
    binding.scanCursor = event.scan_cursor;
  }
}

function transientHttpStatus(status: number | undefined): boolean {
  return status === 408 || status === 429 || (typeof status === "number" && status >= 500);
}
/** Total /history polls a still-running turn gets before the reconcile gives up. */
export const RECONCILE_LIVE_POLL_ATTEMPTS = RECONCILE_FAST_BACKOFFS.length + RECONCILE_SLOW_POLL_COUNT;
// The backend sends a keepalive at least every 15s while a turn runs, so a
// stream silent this long is dead even if its socket never closed: stop
// reading and follow the turn from /history instead.
export const STREAM_SILENCE_MS = 90_000;

/** Yields `source`'s events until it goes quiet for `silenceMs`, then returns
 *  WITHOUT cancelling it: closing the connection would make the backend
 *  interrupt a turn that may still be running. The rest of the stream is read
 *  and discarded until it ends on its own; `onDetached` gets that drain. */
async function* untilSilent<T>(
  source: AsyncIterable<T>,
  silenceMs: number,
  onDetached: (drained: Promise<void>) => void,
): AsyncGenerator<T> {
  const it = source[Symbol.asyncIterator]();
  let detached = false;
  try {
    for (;;) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const silent = new Promise<"silent">((resolve) => {
        timer = setTimeout(() => resolve("silent"), silenceMs);
      });
      const next = it.next();
      const result = await Promise.race([next, silent]);
      clearTimeout(timer);
      if (result === "silent") {
        detached = true;
        onDetached(
          (async () => {
            try {
              if (!(await next).done) while (!(await it.next()).done);
            } catch {
              // The stream ended badly; nothing is listening to it anymore.
            }
          })(),
        );
        return;
      }
      if (result.done) return;
      yield result.value;
    }
  } finally {
    // Any other early exit (an abort, a `break` in the consumer) still
    // releases the stream as a plain for-await would.
    if (!detached) await it.return?.();
  }
}

// Resolve after `ms`, or sooner if the browser fires `online` — so a dropped
// stream's history reconcile retries the instant connectivity returns instead
// of waiting out the full backoff.
function waitOnlineOrDelay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("online", finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    window.addEventListener("online", finish);
  });
}

export type ConversationKind = "live" | "legacy";

// A message the user typed while a turn was streaming, held client-side and
// sent when the agent goes idle. Attachments are kept whole (in memory) so a
// queued send is indistinguishable from a direct one.
export interface QueuedKoryMessage {
  id: string;
  text: string;
  attachments?: KoryComposerAttachment[];
}

export interface SendKoryMessageOptions {
  startFresh?: boolean;
  attachments?: KoryComposerAttachment[];
  /**
   * Trusted, caller-provided context sent only to the model. It is deliberately
   * kept out of the local user message so workflow instructions never read as
   * though the user typed them.
   */
  hiddenContext?: string;
  /** The `<search-results>` block of type-ahead results showing when the turn was sent (model-only). */
  searchResults?: string | null;
  /** Opt out of ambient page capture for purpose-built chat surfaces. */
  includePageContext?: boolean;
  /** Opt out of ambient cell capture for purpose-built chat surfaces. */
  includeCellContext?: boolean;
  /** Reject rather than fall back to the current conversation if fresh creation fails. */
  requireFreshConversation?: boolean;
  /** Receives the newly-created conversation ID before the first turn streams. */
  onConversationCreated?: (conversationId: string) => void;
}

type KoryContextType = {
  messages: KoryMessage[];
  isStreaming: boolean;
  // True while a turn not streaming here is followed from /history (a dropped
  // stream, or a conversation opened mid-turn whose snapshot was `complete: false`).
  isRecoveringTurn: boolean;
  isThinking: boolean;
  // True while switching INTO an existing conversation, before its history
  // arrives. Lets the panel show a skeleton instead of the "New Chat" hub.
  isLoadingConversation: boolean;
  // The conversation whose history the last switch actually read. A failed read
  // leaves `messages` empty and the loading flag cleared, so an empty panel is
  // only an empty conversation when this names it.
  loadedConversationId?: string | null;
  // ID of the conversation the panel is showing. `null` means none has been
  // resolved yet (fresh login before sidebar hydrates, or launcher-only
  // user who hasn't opened /agent). When `null`, sendMessage lets the
  // backend pick/create one via /kory/conversations resolution.
  activeConversationId: string | null;
  // "legacy" when the active conversation is an imported chat.hickory.ai
  // record (read-only). "live" for everything else, including no active
  // conversation. Drives the read-only banner + hides the input.
  activeConversationKind: ConversationKind;
  // Switch the panel to a specific conversation: re-fetches that
  // conversation's history and replaces `messages`. Pass `null` to clear
  // local state without server-side effect. ``kind`` defaults to "live";
  // pass "legacy" when the click came from a legacy sidebar row.
  setActiveConversation: (id: string | null, kind?: ConversationKind) => Promise<void>;
  // Rereads the open conversation's history in place (no skeleton), so turns
  // another surface added (the tray, a voice call) appear. A turn streaming or
  // reconciling here wins (resolves false: skipped, try again once it ends);
  // a switch while it reads drops the result.
  refreshConversation?: () => Promise<boolean>;
  // Follow a conversation's turn from /history until it completes: a card decided
  // from history resumes a turn no stream is relaying. A no-op once the person has
  // moved to another conversation, or while a turn streams here.
  followTurn?: (conversationId: string) => void;
  // Resolves `false` when the message was NOT accepted (dropped before it
  // could be shown/queued), so the composer can restore the draft instead of
  // eating it; any other resolution (void/true) means accepted.
  sendMessage: (
    content: string,
    opts?: SendKoryMessageOptions,
  ) => Promise<void | boolean>;
  // Persist (or toggle off) the user's thumbs vote on the assistant turn at
  // `messageIndex`. Optimistic — reverts on failure.
  submitFeedback: (messageIndex: number, rating: FeedbackRating) => Promise<void>;
  // Create a brand-new conversation row, switch the panel to it, return
  // its id. Used by the sidebar's "New Chat" button.
  startNewConversation: () => Promise<string | null>;
  // Archive the user's current conversation (and its Anthropic session),
  // create a fresh one, switch the panel to it. Used by the model-banner
  // "Start new chat" affordance that fires when the user's preferred Kory
  // model has diverged from the active session's pinned model. Returns the
  // archived session id (for toasts/logging) plus the new conversation id.
  archiveAndStartNew: () => Promise<{
    archived_session_id: string | null;
    new_conversation_id: string | null;
  } | null>;
  clearMessages: () => void;
  stopStreaming: () => void;
  // One-shot Artifact target and HTML injected as hidden context on the next
  // send. Consumed after that send so later turns do not repeat the document.
  setInitialArtifactEdit: (edit: {
    html: string;
    artifactId: string;
    conversationId: string;
  } | null) => void;
  // The cell the user last clicked — a normal table click captures it as Kory
  // context (no selection mode, no extra action). Kept as an array so the wire
  // format + future multi-cell stay open, but a click REPLACES it (one chip).
  attachedCells: KoryCellContext[];
  // Capture a clicked cell as context (replaces the current one).
  attachCell: (cell: KoryCellContext) => void;
  removeCell: (id: string) => void;
  clearCells: () => void;
  // Right-click "Ask Kory about this" menu state + controls.
  cellMenu: { x: number; y: number; cell: KoryCellContext } | null;
  openCellMenu: (x: number, y: number, cell: KoryCellContext) => void;
  closeCellMenu: () => void;
  // Imperatively open the header launcher (or, where it shows, the global
  // tray) in place instead of routing to /kory. Both watch
  // ``launcherOpenNonce``: a bump is an open request.
  openLauncher: () => void;
  launcherOpenNonce: number;
  // The last open request an entry point acted on. Whichever of the two mounts
  // next opens only for a newer one, so a request made while the tray flag
  // loads still opens, and one the other already took never replays.
  launcherHandledNonce: MutableRefObject<number>;
  // Bridge for the L10 runner: while a scorecard section is mounted it registers
  // a handler here so the (app-root, provider-less) cell menu can raise an IDS
  // issue from a right-clicked cell. ``null`` everywhere else, which hides the
  // menu item outside the runner. See ``KoryCellContextMenu`` + ``ScorecardSection``.
  cellIssueHandler: ((cell: KoryCellContext) => void) | null;
  registerCellIssueHandler: (fn: ((cell: KoryCellContext) => void) | null) => void;
  // "Start KPI Investigation" dialog state: the right-clicked cell + which
  // direction the user picked in the menu's submenu, or null (closed). The
  // app-root ``KpiInvestigationDialog`` renders from this.
  kpiInvestigationDraft: { cell: KoryCellContext; direction: "high" | "low" } | null;
  openKpiInvestigation: (cell: KoryCellContext, direction: "high" | "low") => void;
  closeKpiInvestigation: () => void;
  // Message queueing while a turn streams (main Kory surface). The Managed
  // Agents session takes one user.message per run, so the queue is client-
  // side hold-and-drain: enqueueMessage stacks messages typed mid-stream and
  // a drain effect sends them in order when the agent goes idle. Each item
  // is editable/deletable from the queue drawer until it fires. Optional
  // because Kory Code provides this context too but queues on its RELAY
  // (via KoryCodeContext) — it doesn't stub these.
  queuedMessages?: QueuedKoryMessage[];
  enqueueMessage?: (text: string, opts?: { attachments?: KoryComposerAttachment[] }) => void;
  updateQueuedMessage?: (id: string, text: string) => void;
  removeQueuedMessage?: (id: string) => void;
  queueDrawerOpen?: boolean;
  setQueueDrawerOpen?: (open: boolean) => void;
  // True while a fresh-chat send waits on its conversation POST: the question
  // has left the composer but isStreaming is not yet set.
  isStartingTurn?: () => boolean;
};

// Exported so the standalone Kory Code page (src/kory-code/) can provide a
// compatible value to the same chat components without mounting KoryProvider.
export const KoryContext = createContext<KoryContextType | undefined>(undefined);

export function KoryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState<KoryMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  // True while an existing conversation's history fetch is in flight (set in
  // setActiveConversation). The panel renders a skeleton instead of the empty
  // "New Chat" hub during this window, so opening a chat doesn't flash the hub
  // before its messages arrive.
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [loadedConversationId, setLoadedConversationId] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeConversationKind, setActiveConversationKind] = useState<ConversationKind>("live");
  // One-shot Artifact edit context for "Edit in chat". The target instruction
  // and full authored document are prepended to the next outgoing message.
  const [initialArtifactEdit, setInitialArtifactEdit] = useState<{
    html: string;
    artifactId: string;
    conversationId: string;
  } | null>(null);
  // Messages typed while a turn streams, held client-side and drained in
  // order when the agent goes idle (the Managed Agents session takes one
  // user.message per run, so there's no server-side queue to lean on —
  // unlike Kory Code, whose relay owns its queue). Cleared on conversation
  // transitions: a queued follow-up was composed for that thread.
  const [queuedMessages, setQueuedMessages] = useState<QueuedKoryMessage[]>([]);
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);
  // True while recoverDroppedTurn reconciles a dropped/incomplete turn from
  // history — the agent may still be generating server-side, so the queue
  // drain holds until the reconcile settles.
  const [isRecoveringTurn, setIsRecoveringTurn] = useState(false);
  // Set when a reconcile exhausted its polling budget without confirming the
  // run complete: the server may still be generating, so draining a queued
  // message could stack a second run on the session (or get it rejected and
  // lost). Parks the queue until the session's state is known again — a run
  // the server completes (`done`, or a confirmed-complete /history snapshot)
  // or a conversation transition lifts the hold.
  const [queueDrainHeld, setQueueDrainHeld] = useState(false);
  // Cell-selection mode + the cells attached to the next outgoing message.
  const [attachedCells, setAttachedCells] = useState<KoryCellContext[]>([]);
  // Mirror of attachedCells so sendMessage can read a cell attached in the
  // same tick (Explain on the context menu attaches then sends immediately).
  const attachedCellsRef = useRef<KoryCellContext[]>([]);
  const replaceAttachedCells = useCallback((next: KoryCellContext[]) => {
    attachedCellsRef.current = next;
    setAttachedCells(next);
  }, []);
  // Right-click "Ask Kory about this" menu: the cell + cursor coords, or null.
  const [cellMenu, setCellMenu] = useState<{ x: number; y: number; cell: KoryCellContext } | null>(
    null,
  );
  const openCellMenu = useCallback((x: number, y: number, cell: KoryCellContext) => {
    // Inside an in-app ``?embed=1`` iframe (an L10 route-link board), hand the
    // menu to the PARENT window — the actions' landing surfaces (header
    // launcher, KPI-investigation dialog, meeting composer) live there; the
    // chromeless embed shell mounts none of them, so a local menu's items
    // would silently no-op or navigate the framed board away.
    if (postCellMenuToParent(x, y, cell)) return;
    setCellMenu({ x, y, cell });
  }, []);
  const closeCellMenu = useCallback(() => setCellMenu(null), []);

  // Parent side of the same bridge: a marked embed iframe forwarded a cell
  // capture — open our menu at the frame-offset coordinates. Validation
  // (origin, shape, marked-iframe source) lives in the bridge module.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const bridged = bridgedCellMenuFromMessage(event);
      if (bridged) setCellMenu(bridged);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  // Imperative open signal for the header launcher (which owns its own local
  // open state). ``launcherOpenNonce`` is a monotonic counter the launcher
  // watches — bumping it requests an open without us holding the open state
  // here.
  const [launcherOpenNonce, setLauncherOpenNonce] = useState(0);
  const openLauncher = useCallback(() => setLauncherOpenNonce((n) => n + 1), []);
  const launcherHandledNonce = useRef(0);
  // See ``cellIssueHandler`` in the context type. The ``() => fn`` wrapper is
  // load-bearing: a bare ``setCellIssueHandler(fn)`` would treat ``fn`` as a
  // state-updater and call it, not store it.
  const [cellIssueHandler, setCellIssueHandler] = useState<
    ((cell: KoryCellContext) => void) | null
  >(null);
  const registerCellIssueHandler = useCallback(
    (fn: ((cell: KoryCellContext) => void) | null) => setCellIssueHandler(() => fn),
    [],
  );
  // "Start KPI Investigation" dialog: the cell + picked direction, or null.
  const [kpiInvestigationDraft, setKpiInvestigationDraft] = useState<{
    cell: KoryCellContext;
    direction: "high" | "low";
  } | null>(null);
  const openKpiInvestigation = useCallback(
    (cell: KoryCellContext, direction: "high" | "low") => setKpiInvestigationDraft({ cell, direction }),
    [],
  );
  const closeKpiInvestigation = useCallback(() => setKpiInvestigationDraft(null), []);

  // Ambient capture: a document-level right-click / long-press handler so ANY
  // element can opt into "Ask Kory about this" by carrying `data-kory-capture`
  // (the JSON of a makeCellContext() input) — no per-surface React wiring.
  // Raw <td>s, stat tiles, Overview pane rows, and chart points all ride this.
  // Keys off `data-kory-capture` (NOT `data-kory-cell`, which <KoryCell> handles
  // itself), so the two paths never double-fire. Long-press cancels on move so
  // it does not fight scroll on phone.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let startX = 0;
    let startY = 0;
    let suppressClick = false;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const onContextMenu = (e: MouseEvent) => {
      const cell = cellFromCaptureTarget(e.target);
      if (!cell) return;
      e.preventDefault();
      clearTimer();
      openCellMenu(e.clientX, e.clientY, cell);
    };

    const onTouchStart = (e: TouchEvent) => {
      const cell = cellFromCaptureTarget(e.target);
      if (!cell) return;
      const touch = e.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      suppressClick = false;
      clearTimer();
      timer = setTimeout(() => {
        suppressClick = true;
        openCellMenu(startX, startY, cell);
      }, KORY_LONG_PRESS_MS);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!timer) return;
      const touch = e.touches[0];
      if (!touch) return;
      if (Math.hypot(touch.clientX - startX, touch.clientY - startY) > KORY_LONG_PRESS_MOVE_PX) {
        clearTimer();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      // preventDefault stops the compatibility click from activating the
      // capture target (a rock row would otherwise navigate). That click
      // never arrives, so reset suppressClick here — waiting for onClick
      // left the flag true and swallowed the next ordinary tap on the page.
      if (suppressClick) {
        e.preventDefault();
        suppressClick = false;
      }
      clearTimer();
    };

    const onClick = (e: MouseEvent) => {
      if (!suppressClick) return;
      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    };

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);
    document.addEventListener("click", onClick, true);
    return () => {
      clearTimer();
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
      document.removeEventListener("click", onClick, true);
    };
  }, [openCellMenu]);

  const abortRef = useRef<AbortController | null>(null);
  // Set by Stop before the in-flight reader is aborted. A conversation switch
  // aborts without setting this, so leaving the thread does not interrupt.
  const stopRequestedRef = useRef(false);
  // Bumped on every send and on Stop. A reader window captures the value it
  // started with; a later window must not clear that turn's flags or write
  // into its bubble.
  const turnGenerationRef = useRef(0);
  // Stop fences this conversation only. A send on a different conversation
  // keeps following its own turn.
  const stoppedConversationIdRef = useRef<string | null>(null);
  // Set when Stop's request fails. An explicit reopen of this conversation
  // may read the turn the provider is still running.
  const stopMissedConversationIdRef = useRef<string | null>(null);
  // Bumped only when a send starts a turn in that conversation. Stop and
  // opening a chat also advance turnGenerationRef, and a send in another
  // chat must not look like a new turn here.
  const sendEpochByConversationRef = useRef(new Map<string, number>());
  // One entry per Stop whose /interrupt is still open. A later Stop must not
  // replace an earlier entry, or the earlier failure lands on the later answer.
  const pendingStopsRef = useRef<PendingStop[]>([]);
  const turnBindingRef = useRef<TurnBinding>(emptyTurnBinding(null, null));
  // recoverDroppedTurn is declared above followTurn. A still-open turn hands
  // back through this ref so the two cannot close over each other.
  const followTurnRef = useRef<
    (
      assistantId: string | null,
      conversationId: string,
      preferResume?: boolean,
      allowPollResume?: boolean,
    ) => Promise<void>
  >(async () => {});
  // Streams the silence watchdog stopped reading but left open (see
  // untilSilent). Leaving the conversation still stops their turns.
  const detachedRef = useRef(new Set<AbortController>());
  // Token for an in-flight post-drop history reconcile, so a new turn or a
  // conversation switch can cancel it — a stale reconcile must never overwrite
  // newer messages.
  const reconcileRef = useRef<{ cancelled: boolean } | null>(null);
  // Flipped true the first time the user explicitly switches or creates a
  // conversation. Hydration's deferred state writes check this so they
  // don't clobber a navigation that landed mid-fetch.
  const userHasInteracted = useRef(false);
  // Tracks the most-recently-requested conversation id so a slow history
  // fetch can't overwrite a newer switch. Compared after each await; if it
  // no longer matches, the resolution is stale and dropped.
  const pendingConversationIdRef = useRef<string | null>(null);

  // Hydrate from the user's most-recent non-archived conversation when they
  // log in. The sidebar will reconfirm + display the full list separately.
  // Both state writes happen atomically after both awaits resolve — otherwise
  // a user click between the two awaits would race with the second fetch's
  // setMessages and overwrite the conversation they navigated to.
  useEffect(() => {
    // A different login must not resume with the previous person's turn.
    turnBindingRef.current = emptyTurnBinding(null, null);
    if (!user) {
      setMessages([]);
      setActiveConversationId(null);
      setActiveConversationKind("live");
      setIsLoadingConversation(false);
      setQueuedMessages([]);
      userHasInteracted.current = false;
      return;
    }
    let cancelled = false;
    userHasInteracted.current = false;
    (async () => {
      try {
        const list = (await fetchApi("/kory/conversations")) as ConversationListResponse;
        if (cancelled || userHasInteracted.current || !list?.conversations) return;
        const active = list.conversations.find((c) => !c.archived);
        if (!active) return;
        const history = (await fetchApi(`/kory/conversations/${active.id}/history`)) as {
          messages?: RawHistoryMessage[];
          complete?: boolean;
          relayed_through?: string | null;
        };
        // Both writes happen atomically after both awaits resolve, and only
        // if the user hasn't already navigated to a specific conversation.
        if (cancelled || userHasInteracted.current) return;
        setActiveConversationId(active.id);
        setActiveConversationKind("live");
        if (history?.messages) {
          setMessages(parseHistoryMessages(history.messages));
        }
        // Logged in mid-turn: resume that provider turn. The history just
        // loaded is the snapshot, so the resume does not spend another read
        // before the first window.
        if (history?.complete === false) {
          // The history snapshot is on screen. Resume after its last event.
          turnBindingRef.current = emptyTurnBinding(null, active.id);
          const watermark = typeof history.relayed_through === "string" ? history.relayed_through.trim() : "";
          turnBindingRef.current.relayedThrough = watermark || null;
          void followTurn(null, active.id, true);
        }
      } catch {
        // History/list fetch failures shouldn't block the UI; user can start fresh.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);
  const turnInFlight = () => abortRef.current !== null || reconcileRef.current !== null;

  // History replacement gives the stopped answer a server id. Keep the same
  // assistant slot so a late Stop failure still lands on that answer. A slot
  // the snapshot does not contain yet must not fall onto an older answer.
  const retargetStoppedAssistant = useCallback((next: KoryMessage[], conversationId: string) => {
    const assistants = next.filter((message) => message.role === "assistant");
    for (const pending of pendingStopsRef.current) {
      if (pending.conversationId !== conversationId) continue;
      const capturedId = pending.assistantId;
      const ordinal = pending.ordinal;
      if (!capturedId || ordinal == null) continue;
      if (next.some((message) => message.id === capturedId)) continue;
      // A slot the snapshot does not contain yet must not fall onto an older answer.
      pending.assistantId = assistants[ordinal]?.id ?? null;
    }
  }, []);

  const refreshConversation = useCallback(async () => {
    const id = activeConversationIdRef.current;
    if (!id || activeConversationKind !== "live") return true;
    if (turnInFlight()) return false;
    try {
      const history = (await fetchApi(`/kory/conversations/${id}/history`)) as { messages?: RawHistoryMessage[] };
      if (activeConversationIdRef.current !== id || !history?.messages) return true;
      if (turnInFlight()) return false;
      const parsed = parseHistoryMessages(history.messages);
      retargetStoppedAssistant(parsed, id);
      setMessages((prev) => unlessUnchanged(prev, keepStopMissedWarnings(prev, parsed)));
    } catch {
      // The next refresh tries again; what's on screen stays.
    }
    return true;
  }, [activeConversationKind, retargetStoppedAssistant]);

  // Switching conversations or starting a fresh one cancels any in-flight
  // stream — the user moved on, and leaving isStreaming=true would lock
  // KoryInput in the new conversation until the old stream finished.
  //
  // Every conversation-transition entry point (setActiveConversation,
  // startNewConversation, archiveAndStartNew, clearMessages, sendMessage's
  // startFresh) calls this first, so it's also the single place to reset
  // isLoadingConversation: a transition supersedes any in-flight history fetch
  // whose finally() can no longer clear the flag (its conversation id no longer
  // owns pendingConversationIdRef), which would otherwise leave the skeleton
  // stuck. setActiveConversation re-sets it true right after, for the load it
  // actually starts.
  const abortInFlightStream = useCallback(() => {
    turnGenerationRef.current += 1;
    // Conversation, new-chat, and archive transitions all come through here.
    // Drop the previous turn before the next one can resume or be stopped.
    turnBindingRef.current = emptyTurnBinding(null, null);
    abortRef.current?.abort();
    abortRef.current = null;
    for (const controller of detachedRef.current) controller.abort();
    detachedRef.current.clear();
    // Cancel any post-drop reconcile in flight — switching/clearing means its
    // result would land on the wrong conversation.
    if (reconcileRef.current) reconcileRef.current.cancelled = true;
    reconcileRef.current = null;
    setIsStreaming(false);
    setIsThinking(false);
    setIsRecoveringTurn(false);
    setIsLoadingConversation(false);
    setQueueDrainHeld(false);
    // An Artifact edit belongs to the chat that received it. Every
    // conversation transition comes through this reset, so the hidden target
    // can never ride into a different chat after a sidebar click or New Chat.
    setInitialArtifactEdit(null);
  }, []);

  // Recover a dropped stream (connection closed with no `done` event) by
  // reconciling from server-persisted history: Managed Agents persisted the
  // turn's events, so /history holds the authoritative final-or-partial state.
  // The POST stream is one-shot and can't be resumed, so we replace the
  // in-flight turn with the persisted truth rather than re-stream. Bounded
  // backoff (wakes early on `online`); on total failure the turn is marked
  // "lost" with a one-tap retry. Cancelled by a new turn / conversation switch.
  // A null `assistantId` follows a run that was already live when its history
  // loaded (reload or navigation mid-turn): that history is already on screen.
  const recoverDroppedTurn = useCallback(
    async (
      assistantId: string | null,
      conversationId: string | null,
      content: string,
      // True when the backend ended the turn non-terminally (`done` with
      // `incomplete`). The agent may still be finishing server-side, so keep
      // polling /history until it reports the turn complete instead of
      // freezing on the first (partial) snapshot.
      pollUntilComplete = false,
      // True when the backend already finished the turn (`done`) but its reply
      // never reached us. Nothing runs server-side, so this only fills the
      // reply in: no reconnecting state, and no retry if it can't.
      settled = false,
      // A dropped or busy resume left the provider turn open. History can show
      // that, but only another reader can answer a later requires_action.
      // One handoff per recovery; the reader does not ask for a second.
      resumeIfStillOpen = false,
    ) => {
      const token = { cancelled: false };
      reconcileRef.current = token;
      let handedToReader = false;
      // Hold the message-queue drain until this reconcile settles (finally
      // below) — the agent may still be generating server-side, and a queued
      // send would cancel the reconcile (losing the reply it is fetching) and
      // stack a second run on the session.
      setIsRecoveringTurn(true);
      try {
        if (assistantId && !settled) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, reconnect: "reconnecting" as const } : m,
            ),
          );
        }

        // A plain drop starts on the fast schedule only — it is usually already
        // finished server-side. Once /history says the turn is still running
        // (or the backend already said so), follow it on the slow poll until
        // /history reports `complete` — see RECONCILE_SLOW_POLL_COUNT.
        let follow = pollUntilComplete;
        let rendered = assistantId === null;
        // Last event of the snapshot on screen. While following, /history
        // answers `unchanged` until a newer event lands, so a long turn's
        // poll skips the full rebuild.
        let watermark: string | null = null;
        for (let attempt = 0; ; attempt++) {
          const ms =
            attempt < RECONCILE_FAST_BACKOFFS.length
              ? RECONCILE_FAST_BACKOFFS[attempt]
              : follow && attempt < RECONCILE_LIVE_POLL_ATTEMPTS
                ? RECONCILE_SLOW_POLL_MS
                : null;
          if (ms === null) break;
          await waitOnlineOrDelay(ms);
          if (token.cancelled) return;
          try {
            let convId = conversationId;
            if (!convId) {
              // First-turn drop: the stream created the conversation server-side
              // but never told us its id — find it from the just-updated list.
              const list = (await fetchApi("/kory/conversations")) as ConversationListResponse;
              if (token.cancelled) return;
              convId = list?.conversations?.find((c) => !c.archived)?.id ?? null;
            }
            if (!convId) continue;
            const since = follow && watermark ? `?relayed_through=${encodeURIComponent(watermark)}` : "";
            const history = (await fetchApi(`/kory/conversations/${convId}/history${since}`)) as {
              messages?: RawHistoryMessage[];
              complete?: boolean;
              relayed_through?: string | null;
              unchanged?: boolean;
            };
            if (token.cancelled) return;
            // Nothing new since the snapshot on screen: keep it and poll again.
            if (history?.unchanged) continue;
            // This conversation holds at least the turn being recovered, so an
            // empty snapshot is /history's transient-failure fallback, not the
            // outcome — keep what's on screen and retry.
            if (history?.messages?.length) {
              setActiveConversationId(convId);
              setMessages(parseHistoryMessages(history.messages));
              rendered = true;
              watermark = history.relayed_through?.trim() || null;
              // Still generating server-side — show the progress we have and keep
              // polling for the rest of the answer.
              if (history.complete === false) {
                follow = true;
                // The snapshot is on screen. A later pause still needs the
                // same-turn reader: this recovery sends no user message.
                if (resumeIfStillOpen && !handedToReader && !token.cancelled) {
                  handedToReader = true;
                  // History replaced the bubble. Continue after the last event
                  // that snapshot includes. The watermark from the earlier
                  // window is behind that text, so keeping it appends the
                  // snapshot again.
                  if (turnBindingRef.current.conversationId === convId) {
                    turnBindingRef.current.relayedThrough = history.relayed_through?.trim() || null;
                  }
                  if (reconcileRef.current === token) reconcileRef.current = null;
                  setIsRecoveringTurn(false);
                  void followTurnRef.current(null, convId, true, false);
                  return;
                }
                continue;
              }
              // An explicit confirmed-complete run also settles any queue hold
              // left by an earlier timed-out reconcile.
              if (history.complete === true) setQueueDrainHeld(false);
              return;
            }
          } catch {
            // Transient — keep retrying through the backoff schedule.
          }
        }
        if (token.cancelled) return;
        if (settled) {
          // The turn finished but its reply never loaded: say so rather than
          // leave only the tool cards. No retry — resending would run it again.
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, reconnect: "lost" as const } : m)),
          );
          return;
        }
        // Reaching here means the budget ran out without a confirmed outcome —
        // every confirmed exit returns inside the loop (on the first snapshot
        // whose `complete` is no longer false).
        // Whether /history kept answering complete:false or never answered at
        // all, the server-side run was never confirmed over: park the queue.
        // The finally below releases the recovery gate, and an auto-drain
        // against a possibly-live run could stack a second run on the session
        // or lose the follow-up.
        setQueueDrainHeld(true);
        // Polled out the budget but the partial answer is already on screen — keep
        // it rather than masking it behind a "lost" retry banner.
        if (rendered) return;
        // Couldn't recover (likely still offline). Mark the turn so the UI can
        // offer a retry, and stop tool calls masquerading as running.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  reconnect: "lost" as const,
                  retryContent: content,
                  toolCalls: m.toolCalls?.map((tc) => settleRunningToolCall(tc, "error")),
                }
              : m,
          ),
        );
      } finally {
        // Only the invocation that still owns the reconcile may release the
        // drain gate: a cancelled recovery can wake AFTER a newer recovery
        // has already started (new send → stream drops → recovery B), and an
        // unconditional clear here would drop the gate B is holding, letting
        // the queue drain mid-reconcile. Cancellers (abortInFlightStream,
        // sendMessage's supersede block) take ownership of the ref and reset
        // the gate themselves.
        if (reconcileRef.current === token) {
          reconcileRef.current = null;
          setIsRecoveringTurn(false);
        }
      }
    },
    [],
  );

  // Keep reading the same turn after a reader window ends. Resume sends no
  // user message and carries the provider session, turn token, scan cursor,
  // and the last event already shown. A busy or dropped resume waits, then
  // reads the same turn again: /history cannot answer a later pause. Stop
  // fences this conversation only. `allowPollResume` is false on the reader
  // that history just handed back, so a second failure does not loop.
  const followTurn = useCallback(
    async (
      assistantId: string | null,
      conversationId: string,
      preferResume = false,
      allowPollResume = true,
    ) => {
      const generation = turnGenerationRef.current;
      // Stop is the conversation it named. A global stop flag must not block
      // a different unfinished conversation opened afterwards.
      if (stoppedConversationIdRef.current !== conversationId) stopRequestedRef.current = false;
      const owns = () =>
        turnGenerationRef.current === generation &&
        stoppedConversationIdRef.current !== conversationId &&
        (pendingConversationIdRef.current == null || pendingConversationIdRef.current === conversationId) &&
        (activeConversationIdRef.current == null || activeConversationIdRef.current === conversationId);

      const release = () => {
        if (!owns()) return;
        setIsStreaming(false);
        setIsThinking(false);
      };

      if (!owns()) return;
      // This resume is for this conversation only. A turn left open on the
      // previous one must not supply the token, session, cursor, or watermark.
      if (turnBindingRef.current.conversationId !== conversationId) {
        turnBindingRef.current = emptyTurnBinding(null, conversationId);
      }

      setIsStreaming(true);
      let streamIncomplete = false;
      let receivedDone = false;
      let cancelledDone = false;
      let httpError: string | null = null;
      let aborted = false;
      let windowFault: "none" | "busy" | "transient" = "none";

      // A named assistant that is no longer on screen belongs to an older
      // window. Do not fall back onto whatever assistant is current.
      const assistantOn = (prev: KoryMessage[]): string | null => {
        if (assistantId) return prev.some((m) => m.id === assistantId) ? assistantId : null;
        // A later question with no assistant after it needs its own bubble.
        // The previous answer stays on the previous question.
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].role === "user") return null;
          if (prev[i].role === "assistant") return prev[i].id;
        }
        return null;
      };

      const paint = (apply: (message: KoryMessage) => KoryMessage) => {
        if (!owns()) return;
        setMessages((prev) => {
          if (!owns()) return prev;
          const id = assistantOn(prev);
          if (id) return prev.map((m) => (m.id === id ? apply(m) : m));
          // A named bubble that left the screen belongs to an older window.
          // A resume opened on user-only history has no bubble yet: the first
          // event creates one, and later events in this update see it.
          if (assistantId) return prev;
          const created: KoryMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: "",
            toolCalls: [],
            timestamp: new Date(),
          };
          return [...prev, apply(created)];
        });
      };

      const peekHistory = async () => {
        try {
          return (await fetchApi(`/kory/conversations/${conversationId}/history`)) as {
            messages?: RawHistoryMessage[];
            complete?: boolean;
          };
        } catch {
          return null;
        }
      };

      const settleFromHistory = (history: { messages?: RawHistoryMessage[]; complete?: boolean } | null) => {
        if (!owns() || history?.complete !== true || !history.messages?.length) return false;
        setMessages(parseHistoryMessages(history.messages));
        release();
        return true;
      };

      const runWindow = async (): Promise<typeof windowFault> => {
        streamIncomplete = false;
        receivedDone = false;
        cancelledDone = false;
        httpError = null;
        aborted = false;
        let fault: typeof windowFault = "none";
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const source = streamSSE(
            "/kory/stream",
            resumeRequestBody(conversationId, turnBindingRef.current),
            controller.signal,
          );
          for await (const event of untilSilent(source, STREAM_SILENCE_MS, (drained) => {
            detachedRef.current.add(controller);
            void drained.finally(() => detachedRef.current.delete(controller));
          })) {
            if (controller.signal.aborted || !owns()) {
              aborted = true;
              break;
            }
            rememberRelayProgress(turnBindingRef.current, event);
            // The tray paints a proposal from the same tool events as a live send.
            liveCardFromStream(event);
            if (event.type === "conversation") {
              if (event.session_id) turnBindingRef.current.sessionId = event.session_id;
              if (event.turn_token) turnBindingRef.current.turnToken = event.turn_token;
              continue;
            }
            if (event.type === "busy") {
              fault = "busy";
              break;
            }
            if (event.type === "done") {
              if (event.cancelled) cancelledDone = true;
              else if (event.incomplete) streamIncomplete = true;
              else receivedDone = true;
              if (event.model) paint((m) => ({ ...m, model: event.model }));
              break;
            }
            if (event.type === "thinking_start") {
              if (owns()) setIsThinking(true);
              continue;
            }
            if (event.type === "thinking_end") {
              if (owns()) setIsThinking(false);
              continue;
            }
            if (event.type === "model" && event.model) {
              paint((m) => ({ ...m, model: event.model }));
              continue;
            }
            if (event.type === "working") {
              paint((m) => ({ ...m, workingNote: event.content }));
              continue;
            }
            if (event.type === "error") {
              const text = event.content ?? "Something went wrong.";
              paint((m) => ({ ...m, content: `${m.content}\n\n**Error:** ${text}` }));
              continue;
            }
            if (event.type === "text") {
              if (owns()) setIsThinking(false);
              paint((m) => applyRelayEvent(m, event));
              continue;
            }
            if (
              event.type === "tool_start" ||
              event.type === "tool_result" ||
              event.type === "write_confirmation" ||
              event.type === "write_confirmation_auto" ||
              event.type === "write_confirmation_pending" ||
              event.type === "write_confirmation_resolved"
            ) {
              paint((m) => applyRelayEvent(m, event));
            }
          }
        } catch (err) {
          const e = err as Error & { http?: boolean; status?: number };
          if (e.name === "AbortError" || controller.signal.aborted) aborted = true;
          else if (e.http && !transientHttpStatus(e.status)) httpError = e.message || "Something went wrong.";
          else fault = "transient";
        } finally {
          if (abortRef.current === controller) abortRef.current = null;
        }
        return fault;
      };

      // A drop peeks history before opening another reader. An incomplete
      // window already knows the provider turn is still going, so it resumes
      // immediately and does not spend a history read. The peek itself waits
      // one turn of the event loop so the snapshot that just painted (an
      // opened or reloaded conversation still running) stays visible instead
      // of being replaced by the next history read in the same flush.
      if (!preferResume) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (!owns()) return;
        if (settleFromHistory(await peekHistory())) return;
        if (!owns()) return;
      }

      let attempts = 0;
      let faultAttempts = 0;
      for (;;) {
        windowFault = await runWindow();
        if (!owns()) return;
        if (aborted) {
          release();
          return;
        }
        if (cancelledDone) {
          paint((m) => settleOpenTurn(m, "cancelled", "stopped"));
          release();
          return;
        }
        if (httpError) {
          const message = httpError;
          paint((m) => settleOpenTurn({ ...m, content: `${m.content}\n\n**Error:** ${message}` }, "error", "expired"));
          release();
          return;
        }
        if (receivedDone && !streamIncomplete) break;
        if (streamIncomplete) {
          attempts = 0;
          faultAttempts = 0;
          continue;
        }
        if (windowFault === "busy" || windowFault === "transient") {
          if (settleFromHistory(await peekHistory())) return;
          if (!owns()) return;
          faultAttempts += 1;
          if (faultAttempts >= MAX_REJOINS) {
            release();
            // History can show the open turn. One later reader, after the
            // poll wakes, answers a pause the failed windows never saw.
            void recoverDroppedTurn(
              assistantId,
              conversationId,
              "",
              true,
              false,
              allowPollResume,
            );
            return;
          }
          // Busy means another surface holds the session; a throw means the
          // socket died. Either way the next resume is the same turn. `online`
          // cuts the wait short when connectivity just returned.
          const waitMs = RECONCILE_FAST_BACKOFFS[Math.min(faultAttempts - 1, RECONCILE_FAST_BACKOFFS.length - 1)];
          await waitOnlineOrDelay(waitMs);
          if (!owns()) return;
          continue;
        }
        if (settleFromHistory(await peekHistory())) return;
        if (!owns()) return;
        attempts += 1;
        if (attempts >= MAX_REJOINS) {
          release();
          // An incomplete window, or a conversation opened mid-turn, is still
          // running server-side, so /history keeps polling. A plain drop of a
          // send uses the short backoff and then marks the turn lost.
          // A silent end is the same open turn as a busy window. History
          // cannot answer a later pause, so one reader handoff still runs.
          void recoverDroppedTurn(
            assistantId,
            conversationId,
            "",
            preferResume || assistantId == null,
            false,
            allowPollResume,
          );
          return;
        }
      }

      let settled = false;
      try {
        const history = (await fetchApi(`/kory/conversations/${conversationId}/history`)) as {
          messages?: RawHistoryMessage[];
        };
        if (!owns()) return;
        if (history?.messages?.length) {
          setMessages(parseHistoryMessages(history.messages));
          settled = true;
        }
      } catch {
        settled = false;
      }
      if (owns() && !settled) paint((m) => settleOpenTurn(m, "error", "expired"));
      release();
    },
    [recoverDroppedTurn],
  );
  followTurnRef.current = followTurn;

  const setActiveConversation = useCallback(async (
    id: string | null,
    kind: ConversationKind = "live",
  ) => {
    abortInFlightStream();
    userHasInteracted.current = true;
    // Stop named the conversation that was open. Opening a different one
    // must not keep that stop from following the new turn. Opening the
    // chat whose Stop never landed is the person asking to read it again.
    if (id != null && stopMissedConversationIdRef.current === id) {
      stopMissedConversationIdRef.current = null;
      if (stoppedConversationIdRef.current === id) {
        stoppedConversationIdRef.current = null;
        stopRequestedRef.current = false;
      }
    } else if (stoppedConversationIdRef.current !== id) {
      stopRequestedRef.current = false;
    }
    pendingConversationIdRef.current = id;
    // Clear messages immediately on switch so the panel never shows
    // conversation A's history while activeConversationId already points at B.
    // Without this, a user who types a message during the history await (or
    // after a failed fetch) would post with conversation_id=B but include
    // messages from A — silently corrupting B's record.
    setActiveConversationId(id);
    // followTurn runs before the effect copies this state into the ref.
    // Until then, the reader would still see the chat that was just left.
    activeConversationIdRef.current = id;
    setActiveConversationKind(kind);
    setMessages([]);
    setLoadedConversationId(null);
    // A captured cell is page-scoped compose state; drop it on a conversation
    // change so it can't ride along as stale <cell-context> in the next message.
    replaceAttachedCells([]);
    // Queued follow-ups were composed for the conversation being left — drop
    // them so they can't drain into the one being opened.
    setQueuedMessages([]);
    // Clearing to "no conversation" is a genuine new-chat state, not a load —
    // abortInFlightStream above already reset the flag, so the panel shows the
    // hub, not a skeleton.
    if (id === null) return;
    // Existing conversation: mark loading so the panel renders a skeleton (not
    // the "New Chat" hub) while messages is empty and the history is in flight.
    setIsLoadingConversation(true);
    // Legacy chats are read-only imports; the history endpoint lives on a
    // separate route and the rest of the flow (stream, feedback) is gated
    // on activeConversationKind === "live" below.
    const historyUrl =
      kind === "legacy"
        ? `/kory/legacy-conversations/${id}/history`
        : `/kory/conversations/${id}/history`;
    try {
      const history = await fetchApi(historyUrl);
      // Drop the result if the user clicked another row while this fetch
      // was in flight — otherwise a slow A-then-B with A resolving second
      // would replace B's messages with A's history.
      if (pendingConversationIdRef.current !== id) return;
      if (history?.messages) {
        const parsed = parseHistoryMessages(history.messages);
        retargetStoppedAssistant(parsed, id);
        setMessages(parsed);
      }
      setLoadedConversationId(id);
      // Opened mid-turn (reload or navigation): the server is still generating,
      // so keep polling until the turn completes instead of freezing on this
      // snapshot. A switch or a new turn cancels the poll.
      if (kind === "live" && history?.complete === false) {
        // Opening this chat replaced the previous binding. Keep this snapshot's
        // watermark so the reader does not skip output that lands after it.
        turnBindingRef.current = emptyTurnBinding(null, id);
        const watermark = typeof history?.relayed_through === "string" ? history.relayed_through.trim() : "";
        turnBindingRef.current.relayedThrough = watermark || null;
        void followTurn(null, id, true);
      }
    } catch {
      // Leave the panel blank on transient failure — better than showing
      // stale messages from the previous conversation.
    } finally {
      // Only the most-recent selection clears the loading flag; a superseded
      // fetch (the user already clicked another row) leaves it set so the
      // skeleton stays up for the conversation that replaced it.
      if (pendingConversationIdRef.current === id) {
        setIsLoadingConversation(false);
      }
    }
  }, [abortInFlightStream, followTurn, replaceAttachedCells, retargetStoppedAssistant]);

  // Deep link: `/agent?conversation=<id>` opens that saved conversation
  // directly (e.g. from the heavy-Slack-thread nudge). Runs once the user
  // is loaded so the history fetch is authenticated; `setActiveConversation`
  // flips `userHasInteracted` synchronously, so it preempts the recent-
  // conversation hydration above even if both fire on the same mount. The
  // history endpoint loads by id and is owner-gated, so the target need not
  // be in the sidebar list yet. The param is consumed (stripped from the
  // URL) so a later "New chat" or refresh doesn't snap back to it.
  useEffect(() => {
    if (!user) return;
    const conversationId = searchParams.get("conversation");
    if (!conversationId) return;
    void setActiveConversation(conversationId);
    setSearchParams(
      (prev) => {
        prev.delete("conversation");
        return prev;
      },
      { replace: true },
    );
  }, [user?.id, searchParams, setActiveConversation, setSearchParams]);

  const startNewConversation = useCallback(async (): Promise<string | null> => {
    // Errors propagate so the caller's TanStack Mutation onError fires —
    // swallowing them here would make the "New chat" button silently fail.
    abortInFlightStream();
    userHasInteracted.current = true;
    // Drop queued follow-ups in the same synchronous block as the abort —
    // the abort opens the drain gates, so clearing after the await below
    // would let the drain effect send a stale message into the conversation
    // being left.
    setQueuedMessages([]);
    // Claim the pending slot with a sentinel before the await so a
    // concurrent setActiveConversation can detect that a "new chat" POST
    // is in flight. When the POST resolves we check that the sentinel is
    // still ours; if a sidebar click rewrote pendingConversationIdRef
    // mid-flight, we drop our result rather than overwriting their choice.
    const sentinelId = "__new__";
    pendingConversationIdRef.current = sentinelId;
    const conv = (await fetchApi("/kory/conversations", { method: "POST" })) as {
      id: string;
    };
    if (!conv?.id) return null;
    if (pendingConversationIdRef.current !== sentinelId) return null;
    pendingConversationIdRef.current = conv.id;
    setActiveConversationId(conv.id);
    setActiveConversationKind("live");
    setMessages([]);
    replaceAttachedCells([]);
    return conv.id;
  }, [abortInFlightStream, replaceAttachedCells]);

  const archiveAndStartNew = useCallback(async () => {
    abortInFlightStream();
    userHasInteracted.current = true;
    // Same synchronous queue drop as startNewConversation — see comment there.
    setQueuedMessages([]);
    // Same sentinel + check-after-await dance as startNewConversation so a
    // concurrent setActiveConversation can win the race when a user clicks
    // a sidebar row mid-flight.
    const sentinelId = "__archive_and_new__";
    pendingConversationIdRef.current = sentinelId;
    const resp = (await fetchApi("/kory/new-session", { method: "POST" })) as {
      archived_session_id: string | null;
      new_conversation_id: string | null;
    };
    if (pendingConversationIdRef.current !== sentinelId) return null;
    if (resp.new_conversation_id) {
      pendingConversationIdRef.current = resp.new_conversation_id;
      setActiveConversationId(resp.new_conversation_id);
      setActiveConversationKind("live");
      setMessages([]);
      replaceAttachedCells([]);
    }
    return resp;
  }, [abortInFlightStream, replaceAttachedCells]);

  const clearMessages = useCallback(() => setMessages([]), []);

  const stopStreaming = useCallback(() => {
    // Generation moves first so an older reader cannot clear the next turn's
    // flags after this abort. The fence is this conversation only — a switch
    // calls abortInFlightStream directly and does not interrupt.
    const id = activeConversationIdRef.current;
    stoppedConversationIdRef.current = id;
    stopRequestedRef.current = true;
    const stoppedAssistantId = [...messages].reverse().find((message) => message.role === "assistant")?.id ?? null;
    const assistantMessages = messages.filter((message) => message.role === "assistant");
    const stoppedAssistantOrdinal = stoppedAssistantId
      ? assistantMessages.findIndex((message) => message.id === stoppedAssistantId)
      : -1;
    const pendingStop: PendingStop = {
      assistantId: stoppedAssistantId,
      ordinal: stoppedAssistantOrdinal >= 0 ? stoppedAssistantOrdinal : null,
      conversationId: id,
    };
    setMessages((prev) => prev.map((m) => settleOpenTurn(m, "cancelled", "stopped")));
    if (id && !id.startsWith("__")) {
      pendingStopsRef.current = [...pendingStopsRef.current, pendingStop];
      const binding = turnBindingRef.current;
      const sameConversation = binding.conversationId === id;
      // Opening this chat also advances turnGenerationRef. Only a send in
      // this conversation advances its epoch, so leaving the chat can still
      // record the miss.
      const stoppedSendEpoch = sendEpochByConversationRef.current.get(id) ?? 0;
      const dropPendingStop = () => {
        pendingStopsRef.current = pendingStopsRef.current.filter((item) => item !== pendingStop);
      };
      void fetchApi(`/kory/conversations/${id}/interrupt`, {
        method: "POST",
        body: JSON.stringify(
          sameConversation
            ? {
                turn_token: binding.turnToken,
                provider_session_id: binding.sessionId,
              }
            : {},
        ),
      }).then(
        () => {
          dropPendingStop();
        },
        () => {
          // A newer send can start before this request fails. Keep the error
          // on this Stop's answer, but do not let an older Stop reopen
          // recovery on a newer turn in the same conversation.
          const assistantId = pendingStop.assistantId;
          dropPendingStop();
          const epochNow = sendEpochByConversationRef.current.get(id) ?? 0;
          // The warning is only for the chat on screen. Recovery still has
          // to be recorded when the person has already opened another chat.
          if (epochNow === stoppedSendEpoch) stopMissedConversationIdRef.current = id;
          if (activeConversationIdRef.current !== id || !assistantId) return;
          setMessages((prev) => {
            if (!prev.some((message) => message.id === assistantId)) return prev;
            return prev.map((message) => (message.id === assistantId ? withStopMissedWarning(message) : message));
          });
        },
      );
    }
    abortInFlightStream();
  }, [abortInFlightStream, messages]);

  // -- Cell capture (passive: a normal table click adds the cell as context) --
  const attachCell = useCallback((cell: KoryCellContext) => {
    // Stamp the page-owner key for the current route here — the single choke
    // point every capture path (left-click, right-click menu, ambient
    // data-kory-capture) funnels through — so an investigation to-do can route
    // to the page's technical / business owner without each producer wiring it.
    const stamped: KoryCellContext = {
      ...cell,
      pageKey: cell.pageKey ?? resolvePageOwner(window.location.pathname) ?? undefined,
    };
    // A click ACCUMULATES the cell (up to MAX_ATTACHED_CELLS) so the user can
    // line up several values and ask Kory to compare them. Re-clicking a cell
    // that's already attached is a no-op (no churn / no duplicate chip); past the
    // cap the oldest cell drops off so the newest click always lands.
    // Write the ref synchronously so Explain can attach-then-send in one tick.
    const prev = attachedCellsRef.current;
    if (prev.some((c) => sameCell(c, stamped))) return;
    const next = [...prev, stamped];
    replaceAttachedCells(
      next.length > MAX_ATTACHED_CELLS ? next.slice(next.length - MAX_ATTACHED_CELLS) : next,
    );
  }, [replaceAttachedCells]);
  const removeCell = useCallback(
    (id: string) => replaceAttachedCells(attachedCellsRef.current.filter((c) => c.id !== id)),
    [replaceAttachedCells],
  );
  const clearCells = useCallback(() => replaceAttachedCells([]), [replaceAttachedCells]);

  // -- Message queue (client-side hold-and-drain; see QueuedKoryMessage) -----
  const enqueueMessage = useCallback(
    (text: string, opts?: { attachments?: KoryComposerAttachment[] }) => {
      setQueuedMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), text, attachments: opts?.attachments },
      ]);
    },
    [],
  );
  const updateQueuedMessage = useCallback((id: string, text: string) => {
    setQueuedMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text } : m)));
  }, []);
  const removeQueuedMessage = useCallback((id: string) => {
    setQueuedMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // The drawer has nothing to show once the queue drains — close it.
  useEffect(() => {
    if (queueDrawerOpen && queuedMessages.length === 0) setQueueDrawerOpen(false);
  }, [queueDrawerOpen, queuedMessages.length]);

  const sendMessage = useCallback(
    async (
      content: string,
      opts?: SendKoryMessageOptions,
    ) => {
      // Legacy conversations are read-only imports — the panel hides the
      // input on those, but guard at the action layer too so a stray
      // keyboard shortcut or programmatic call can't fire /stream against
      // a non-routable id. A required fresh send leaves the legacy record
      // for a new live conversation, so it may proceed.
      if (activeConversationKind === "legacy" && !(opts?.startFresh && opts.requireFreshConversation)) return;
      // An explicit send is a user interaction — claim it synchronously (for
      // both the normal and startFresh paths) so the login-time recent-
      // conversation hydration (which only defers to `userHasInteracted`)
      // can't resolve mid-send and replace this turn with old history.
      // Without this, a question typed on the overview page (or the launcher)
      // before hydration settles is dropped on the handoff to /agent.
      userHasInteracted.current = true;
      const turnToken = crypto.randomUUID();
      const sendingConversation = activeConversationId;
      // A send on the conversation that was stopped lifts that fence. A send
      // on a different conversation leaves it, so the stopped turn cannot resume.
      if (stoppedConversationIdRef.current == null || stoppedConversationIdRef.current === sendingConversation) {
        stopRequestedRef.current = false;
        stoppedConversationIdRef.current = null;
        if (stopMissedConversationIdRef.current === sendingConversation) {
          stopMissedConversationIdRef.current = null;
        }
        // This send replaces that Stop. A late interrupt failure must not
        // arm recovery again after the fence is lifted.
        if (sendingConversation) {
          const epoch = sendEpochByConversationRef.current.get(sendingConversation) ?? 0;
          sendEpochByConversationRef.current.set(sendingConversation, epoch + 1);
        }
        for (const pending of pendingStopsRef.current) {
          if (pending.conversationId === sendingConversation) pending.ordinal = null;
        }
      }
      // startFresh aborts first, and that abort clears the binding. Install
      // this turn's binding after that clear so the new token survives.
      // A new turn supersedes any post-drop reconcile still backing off, and
      // clears a stale "Reconnecting…" indicator from an earlier dropped turn.
      if (reconcileRef.current) {
        reconcileRef.current.cancelled = true;
        reconcileRef.current = null;
        // Taking ownership of the ref means the cancelled reconcile's finally
        // won't release the drain gate — do it here.
        setIsRecoveringTurn(false);
        setMessages((prev) =>
          prev.map((m) =>
            m.reconnect === "reconnecting" ? { ...m, reconnect: undefined } : m,
          ),
        );
      }
      // Capture page + cell context up-front so it's consistent between the
      // local message (UI chips) and the outgoing API payload (model reasoning).
      const pageContext = opts?.includePageContext === false ? null : capturePageContext();
      const cells = opts?.includeCellContext === false ? [] : attachedCellsRef.current;
      const attachments = opts?.attachments ?? [];
      const hiddenContext = opts?.hiddenContext;
      const searchResults = opts?.searchResults;
      // Consume the one-shot Artifact edit context on the first send, then
      // clear it so subsequent turns do not re-send the same document.
      const artifactEdit =
        !opts?.startFresh &&
        initialArtifactEdit?.conversationId === activeConversationIdRef.current
          ? initialArtifactEdit
          : null;
      if (initialArtifactEdit) setInitialArtifactEdit(null);

      const userMsg: KoryMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        pageContext: pageContext?.meta,
        cellContext: cells.length ? cells : undefined,
        attachments: attachments.length ? toMessageAttachments(attachments) : undefined,
        timestamp: new Date(),
      };

      const assistantId = crypto.randomUUID();
      const assistantMsg: KoryMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        toolCalls: [],
        timestamp: new Date(),
      };

      // Referencing a value opens a FRESH chat: show just this turn, mint a new
      // conversation, and drop prior history so the question stands on its own.
      // `cells` was snapshotted above, so the captured reference still rides
      // along even though the new conversation starts empty. Follow-up turns
      // (no startFresh) keep appending to the active conversation as before.
      let convId = activeConversationId;
      let history = messages;
      if (opts?.startFresh) {
        abortInFlightStream();
        // Anything queued was aimed at the conversation being left behind.
        setQueuedMessages([]);
        history = [];
        const previousPending = pendingConversationIdRef.current;
        // Claim the slot before clearing messages and before the create
        // request, so a history fetch still in flight for the old conversation
        // cannot write those messages back over this turn.
        pendingConversationIdRef.current = "__fresh__";
        setMessages([userMsg, assistantMsg]);
        try {
          const conv = (await fetchApi("/kory/conversations", { method: "POST" })) as {
            id: string;
          };
          if (pendingConversationIdRef.current !== "__fresh__") {
            // The person opened another chat while this one was being created.
            // Sending now would abort that reader and write into the old chat.
            return false;
          } else if (conv?.id) {
            convId = conv.id;
            pendingConversationIdRef.current = conv.id;
            turnBindingRef.current = emptyTurnBinding(turnToken, conv.id);
            setActiveConversationId(conv.id);
            setLoadedConversationId(conv.id); // a just-created conversation is known to be empty
            setActiveConversationKind("live");
            opts.onConversationCreated?.(conv.id);
          } else if (opts.requireFreshConversation) {
            pendingConversationIdRef.current = previousPending;
            setMessages(messages);
            return false;
          }
        } catch {
          if (pendingConversationIdRef.current !== "__fresh__") return false;
          if (opts.requireFreshConversation) {
            pendingConversationIdRef.current = previousPending;
            setMessages(messages);
            return false;
          }
          // Keep the active conversation if the new-chat POST fails — better
          // than dropping the user's question.
        }
        if (pendingConversationIdRef.current === "__fresh__") pendingConversationIdRef.current = convId;
        if (turnBindingRef.current.turnToken !== turnToken) {
          turnBindingRef.current = emptyTurnBinding(turnToken, convId);
        }
      } else {
        turnBindingRef.current = emptyTurnBinding(turnToken, sendingConversation);
        setMessages((prev) => [...prev, userMsg, assistantMsg]);
      }
      // The attached cells now live on the sent message (chips persist there);
      // clear the pending set so the next turn starts clean. Purpose-built
      // workflows can opt out and leave an unrelated pending capture alone.
      if (opts?.includeCellContext !== false) clearCells();
      // Drop the previous reader before taking a new generation. Abort is
      // synchronous; the old window's finalizer runs after this bump, so it
      // does not clear the flags set just below.
      abortRef.current?.abort();
      abortRef.current = null;
      const generation = ++turnGenerationRef.current;
      if (convId) {
        const epoch = sendEpochByConversationRef.current.get(convId) ?? 0;
        sendEpochByConversationRef.current.set(convId, epoch + 1);
      }
      // This send owns its conversation. Do not move that Stop's slot onto
      // the new assistant when history refreshes. Another chat's Stop stays.
      for (const pending of pendingStopsRef.current) {
        if (pending.conversationId === convId) pending.ordinal = null;
      }
      setIsStreaming(true);
      setIsThinking(true);

      const abortController = new AbortController();
      abortRef.current = abortController;

      // Track how the turn ended so the finally-sweep can mark still-running
      // tool calls honestly (cancelled on abort, error on failure) instead of
      // masquerading them as a successful "complete".
      let streamAborted = false;
      let streamErrored = false;
      // Set on the SSE `done` event; its absence once the loop ends (without an
      // abort or a backend error) means the connection dropped mid-turn — the
      // `finally` then recovers from persisted history instead of falsely
      // marking the turn complete.
      let receivedDone = false;
      // Set when the backend ended the turn non-terminally (`done` with
      // `incomplete: true` — e.g. it gave up at the wall-clock budget while the
      // agent kept working). Routed through the same dropped-turn recovery, but
      // the reconcile polls /history until the turn finishes server-side.
      let streamIncomplete = false;
      // Set when the Kory session answered `busy`: the turn never ran, so the
      // message comes back to the composer instead of sitting here unsent.
      let rejectedBusy = false;
      // Whether the stream carried tool calls, and any reply text (or an error
      // in its place). A `done` after only tool events means the reply never
      // reached us — reconcile it from /history rather than leave it out.
      let sawTools = false;
      let sawReply = false;

      try {
        // Prepend the context blocks to the outgoing payload only — the local
        // chat bubble shows just the user's words (plus chips rendered from
        // userMsg.pageContext / userMsg.cellContext). Cell context goes first
        // (highest signal: it names the exact value the user is asking about),
        // then page context, then the user's text.
        const apiMessages = [...history, userMsg].map((m, i, arr) => {
          const isLast = i === arr.length - 1;
          let body = m.content;
          if (isLast && searchResults) body = `${searchResults}\n\n${body}`;
          if (isLast && pageContext) body = `${pageContext.text}\n\n${body}`;
          if (isLast && cells.length) body = `${serializeCellContextBlock(cells)}\n\n${body}`;
          // Attachment blocks go first: document contents + the image-name
          // manifest. Image bytes ride the request's `images` field below.
          if (isLast && attachments.length) {
            const block = serializeAttachmentsBlock(attachments);
            if (block) body = body ? `${block}\n\n${body}` : block;
          }
          // "Edit in chat" context is model-only. It gives Kory both the exact
          // update target and the complete authored document on the first send.
          if (isLast && artifactEdit) {
            body = `<current-artifact-html>\n${artifactEdit.html}\n</current-artifact-html>\n\n${body}`;
          }
          let trustedContext = hiddenContext;
          if (isLast && artifactEdit) {
            const editInstruction = `Update the existing Artifact by calling write_artifact with artifact_id=${artifactEdit.artifactId}. Keep this as an Artifact unless the user explicitly asks to upgrade it to an App.`;
            trustedContext = trustedContext
              ? `${editInstruction}\n${trustedContext}`
              : editInstruction;
          }
          if (isLast && trustedContext) {
            // Drop a literal closing marker from the data so the block ends only
            // at its own marker — history hides exactly this block (kory_chat.py).
            const safeContext = trustedContext.replaceAll("</trusted-workflow-context>", "");
            body = `<trusted-workflow-context>\n${safeContext}\n</trusted-workflow-context>\n\n${body}`;
          }
          return {
            role: m.role,
            content: encodeBase64Utf8(body),
          };
        });
        const images = imagesPayload(attachments);
        const files = filesPayload(attachments);

        // A stream silent past STREAM_SILENCE_MS ends this loop with no `done`,
        // so the turn is followed from /history like any other drop.
        for await (const event of untilSilent(
          streamSSE(
            "/kory/stream",
            {
              messages: apiMessages,
              content_encoding: KORY_CONTENT_ENCODING,
              ...(images.length ? { images } : {}),
              ...(files.length ? { files } : {}),
              // Pass through when set so the backend writes to the right
              // conversation; otherwise let _resolve_conversation pick/create.
              ...(convId ? { conversation_id: convId } : {}),
              turn_token: turnToken,
            },
            abortController.signal,
          ),
          STREAM_SILENCE_MS,
          (drained) => {
            detachedRef.current.add(abortController);
            void drained.finally(() => detachedRef.current.delete(abortController));
          },
        )) {
          if (abortController.signal.aborted || turnGenerationRef.current !== generation) break;
          rememberRelayProgress(turnBindingRef.current, event);
          if (event.type === "tool_start") sawTools = true;
          if (event.type === "text" || event.type === "error") sawReply = true;

          // A proposal shows in the tray as a skeleton, then as its card.
          liveCardFromStream(event);
          switch (event.type) {
            case "conversation":
              // First send: the server names the conversation it accepted.
              // Do not invent an id, and do not replace one the user already has.
              if (event.conversation_id && !convId) {
                convId = event.conversation_id;
                pendingConversationIdRef.current = convId;
                turnBindingRef.current.conversationId = convId;
                setActiveConversationId(convId);
                setActiveConversationKind("live");
              }
              if (event.session_id) turnBindingRef.current.sessionId = event.session_id;
              if (event.turn_token) turnBindingRef.current.turnToken = event.turn_token;
              break;

            case "thinking_start":
              setIsThinking(true);
              break;

            case "model_upgraded":
              // The pre-flight moved this conversation onto the user's new
              // model before sending — mark the in-flight assistant turn so
              // the thread renders the switch point, and refresh
              // session-status so the "Switching to X on your next message"
              // hint clears now instead of on the next 30s poll.
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, modelUpgrade: { from: event.from_model, to: event.to_model } }
                    : m,
                ),
              );
              queryClient.invalidateQueries({
                queryKey: ["agent-session-status"],
              });
              break;

            case "thinking_end":
              setIsThinking(false);
              break;

            case "working":
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, workingNote: event.content } : m)),
              );
              break;

            case "text":
            case "tool_start":
            case "tool_result":
            case "write_confirmation":
            case "write_confirmation_auto":
            case "write_confirmation_pending":
            case "write_confirmation_resolved":
              if (event.type === "text") setIsThinking(false);
              if (turnGenerationRef.current !== generation) break;
              setMessages((prev) => {
                if (turnGenerationRef.current !== generation) return prev;
                return prev.map((m) => (m.id === assistantId ? applyRelayEvent(m, event) : m));
              });
              break;

            case "busy":
              // The Kory session takes one turn at a time across surfaces; another
              // (the tray, a voice call) holds it, so this message was not sent.
              rejectedBusy = true;
              break;

            case "error":
              // A stream-level error: surface it in the text AND mark any
              // still-running tool call as errored. Without this, the later
              // done/finally sweep would flip those calls to "complete" and the
              // failure would read as success (the silent-erroring this set out
              // to fix). Already-resolved calls keep their status.
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? {
                        ...m,
                        content:
                          m.content +
                          `\n\n**Error:** ${event.content ?? "Something went wrong."}`,
                        toolCalls: m.toolCalls?.map((tc) => settleRunningToolCall(tc, "error")),
                      }
                    : m,
                ),
              );
              break;

            case "done":
              if (event.incomplete) {
                // The reader window ended before the turn did. Leave
                // `receivedDone` false so the finally hands this conversation
                // to followTurn, which resumes the same turn. Do not sweep
                // running tools here. Apply the model badge now so it shows
                // during the next window.
                if (event.model) {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId ? { ...m, model: event.model } : m,
                    ),
                  );
                }
                streamIncomplete = true;
                break;
              }
              receivedDone = true;
              // Backend reports the resolved model on the done event so the
              // assistant turn can render a model badge. We also sweep any
              // tool calls still marked `running` to `complete` here — if a
              // `tool_result` event was missed or its tool_use_id failed to
              // match (different ID scheme between agent.tool_use and
              // agent.tool_result event variants), the call would otherwise
              // stay running forever and keep the "Tapping the trunk"
              // thinking indicator visible after the response is done.
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantId) return m;
                  const next: typeof m = { ...m };
                  if (event.model) next.model = event.model;
                  if (m.toolCalls?.some((tc) => tc.status === "running")) {
                    next.toolCalls = m.toolCalls.map((tc) =>
                      settleRunningToolCall(tc, "complete"),
                    );
                  }
                  // A card still open when the turn ends means its resolved
                  // event was missed — settle it so the buttons don't dangle
                  // on a finished turn.
                  if (m.writeConfirmations?.some((wc) => wc.status === "pending" || wc.status === "deciding")) {
                    next.writeConfirmations = m.writeConfirmations.map((wc) =>
                      wc.status === "pending" || wc.status === "deciding"
                        ? { ...wc, status: "expired" as const }
                        : wc,
                    );
                  }
                  return next;
                }),
              );
              break;
          }
        }
      } catch (err) {
        const e = err as Error & { code?: string; http?: boolean; status?: number };
        if (e.name === "AbortError") {
          streamAborted = true;
        } else if (e.http && !transientHttpStatus(e.status)) {
          // Authorization and other non-transient rejections stay on screen.
          // A 5xx/429 with a known conversation rejoins from `finally`.
          streamErrored = true;
          // Structured stale-session rejection from `_is_session_stale`
          // in kory_chat.py. Refetch /session-status so the banner shows
          // (the user's input was disabled by the banner; this catches
          // the race where a stale frontend slipped a send through
          // before the 30s poll picked up the preference change).
          if (e.code === "stale_session") {
            queryClient.invalidateQueries({
              queryKey: ["agent-session-status"],
            });
          }
          if (turnGenerationRef.current === generation) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + `\n\n**Error:** ${e.message}` }
                  : m,
              ),
            );
          }
        }
        // else: a network failure (no `http` tag) or a transient HTTP status —
        // leave the flags unset so `finally` rejoins the same turn.
      } finally {
        if (abortRef.current === abortController) abortRef.current = null;
        const owns =
          turnGenerationRef.current === generation &&
          (convId == null || stoppedConversationIdRef.current !== convId);
        // A window that ended non-terminally, on a conversation we can name,
        // keeps isStreaming set and resumes. Clearing it here would let the
        // queue inject a second user message between windows. An older window
        // does not clear a newer reader's flags.
        const rejoin =
          owns && Boolean(convId) && !receivedDone && !streamAborted && !streamErrored && !rejectedBusy;
        if (owns && !rejoin) {
          setIsStreaming(false);
          setIsThinking(false);
        }
        // A turn that produced no `done`, wasn't aborted, and hit no hard
        // backend error is a dropped connection. With a conversation id it
        // rejoins the provider turn; otherwise the history poll is the only
        // remaining handle. Do NOT sweep its tools to "complete" here.
        const streamDropped = !receivedDone && !streamAborted && !streamErrored;
        const replyMissing = receivedDone && sawTools && !sawReply;
        // A completed `done` means the server accepted and finished this run,
        // so any post-timeout queue hold is stale — lift it. Errored/aborted
        // sends leave the hold in place (the original run's fate is still
        // unknown, and a drain could stack on it or get rejected and lost).
        if (receivedDone && owns) setQueueDrainHeld(false);
        if (rejoin && convId) {
          void followTurn(assistantId, convId, streamIncomplete);
        } else if (streamDropped && owns) {
          void recoverDroppedTurn(assistantId, convId, content, streamIncomplete);
        } else if (owns) {
          // Resolve any tool call still `running` for paths the `done` handler
          // doesn't reach. Mark it honestly by how the turn ended: cancelled
          // when the user aborted/navigated, error on a backend failure — never
          // silently "complete" (what made failed calls masquerade as success).
          const sweepTo: ToolCallStatus["status"] = streamAborted
            ? "cancelled"
            : streamErrored
              ? "error"
              : "complete";
          setMessages((prev) => {
            if (turnGenerationRef.current !== generation) return prev;
            return prev.map((m) => {
              if (m.id !== assistantId) return m;
              if (!m.toolCalls?.some((tc) => tc.status === "running")) return m;
              return {
                ...m,
                toolCalls: m.toolCalls.map((tc) =>
                  settleRunningToolCall(tc, sweepTo),
                ),
              };
            });
          });
          // The turn finished but only its tool calls reached us — fill the
          // reply in from /history.
          if (replyMissing) void recoverDroppedTurn(assistantId, convId, content, false, true);
        }
      }
      if (rejectedBusy) {
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id && m.id !== assistantId));
        toast.info("Kory is replying somewhere else. Send it again once that reply finishes.");
        return false;
      }
    },
    [
      messages,
      activeConversationId,
      activeConversationKind,
      queryClient,
      recoverDroppedTurn,
      followTurn,
      clearCells,
      abortInFlightStream,
      initialArtifactEdit,
    ],
  );

  // Drain-on-idle: fire the oldest queued message once nothing is running —
  // no local stream, no dropped-turn reconcile, no conversation load in
  // flight, no post-timeout hold (queueDrainHeld: the server may still be
  // running). One message per idle window: sendMessage flips isStreaming true
  // synchronously in the same batch as the dequeue, so the next item waits
  // for the next idle. Conversation transitions clear the queue before this
  // can fire, so a queued send never lands in a different thread.
  useEffect(() => {
    if (isStreaming || isRecoveringTurn || isLoadingConversation || queueDrainHeld) return;
    const next = queuedMessages[0];
    if (!next) return;
    setQueuedMessages((prev) => prev.slice(1));
    void sendMessage(
      next.text,
      next.attachments?.length ? { attachments: next.attachments } : undefined,
    );
  }, [
    isStreaming,
    isRecoveringTurn,
    isLoadingConversation,
    queueDrainHeld,
    queuedMessages,
    sendMessage,
  ]);

  const submitFeedback = useCallback(
    async (messageIndex: number, rating: FeedbackRating) => {
      // No feedback on legacy chats — the kory_message_feedback row would
      // reference a non-existent KoryConversation and the kory_chat
      // endpoint would 404 the conversation_id.
      if (!activeConversationId || activeConversationKind === "legacy") return;
      const previous = messages[messageIndex]?.feedback;
      // Clicking the already-active vote toggles it off.
      const next = previous === rating ? undefined : rating;

      // Optimistic — paint the new vote state immediately.
      setMessages((prev) =>
        prev.map((m, i) => (i === messageIndex ? { ...m, feedback: next } : m)),
      );

      try {
        if (next === undefined) {
          await fetchApi(
            `/kory/feedback?conversation_id=${activeConversationId}&message_index=${messageIndex}`,
            { method: "DELETE" },
          );
        } else {
          await fetchApi("/kory/feedback", {
            method: "POST",
            body: JSON.stringify({
              conversation_id: activeConversationId,
              message_index: messageIndex,
              rating: next,
            }),
          });
        }
      } catch {
        // Revert to the pre-click vote on failure.
        setMessages((prev) =>
          prev.map((m, i) => (i === messageIndex ? { ...m, feedback: previous } : m)),
        );
        toast.error("Couldn't save your feedback");
      }
    },
    [activeConversationId, activeConversationKind, messages],
  );

  // A confirmation answered from history (deliver) has no live reader. Follow
  // that turn through /history. The resume reader above is a different path:
  // it keeps the same Managed Agents turn open after a reader window ends.
  const followDeliveredTurn = useCallback(
    (conversationId: string) => {
      if (activeConversationIdRef.current !== conversationId || turnInFlight()) return;
      void recoverDroppedTurn(null, conversationId, "", true);
    },
    [recoverDroppedTurn],
  );

  useApprovalWaitingNotice(messages);
  // sendMessage replaces this sentinel on every path, success or failure.
  const isStartingTurn = useCallback(() => pendingConversationIdRef.current === "__fresh__", []);

  return (
    <KoryContext.Provider
      value={{
        messages,
        isStreaming,
        isRecoveringTurn,
        isThinking,
        isLoadingConversation,
        loadedConversationId,
        activeConversationKind,
        activeConversationId,
        setActiveConversation,
        refreshConversation,
        followTurn: followDeliveredTurn,
        sendMessage,
        submitFeedback,
        startNewConversation,
        archiveAndStartNew,
        clearMessages,
        stopStreaming,
        setInitialArtifactEdit,
        attachedCells,
        attachCell,
        removeCell,
        clearCells,
        cellMenu,
        openCellMenu,
        closeCellMenu,
        openLauncher,
        launcherOpenNonce,
        launcherHandledNonce,
        cellIssueHandler,
        registerCellIssueHandler,
        kpiInvestigationDraft,
        openKpiInvestigation,
        closeKpiInvestigation,
        queuedMessages,
        enqueueMessage,
        updateQueuedMessage,
        removeQueuedMessage,
        queueDrawerOpen,
        setQueueDrawerOpen,
        isStartingTurn,
      }}
    >
      {children}
    </KoryContext.Provider>
  );
}

export function useKory() {
  const context = useContext(KoryContext);
  if (context === undefined) {
    throw new Error("useKory must be used within a KoryProvider");
  }
  return context;
}

/**
 * Non-throwing accessor — returns the Kory context, or undefined when there's no
 * KoryProvider above (a data table rendered in isolation, a unit test). Used by
 * KoryCell so a cell can render anywhere; capture is a no-op without a provider
 * rather than a crash.
 */
export function useKoryOptional() {
  return useContext(KoryContext);
}
